import { Dispatch, SetStateAction } from 'react';
import { AxiosError, CanceledError } from 'axios';
import http from '../shared/HttpCommon';
import { CommunityManagement } from '../shared/interfaces/ICommunityManagement';

const getLastScores = async (
  location: string,
  loading: Dispatch<SetStateAction<boolean>>,
  signal: AbortSignal
) => {
  try {
    loading(true);
    const { data } = await http.post('/google/scores/last', { location }, { signal });
    loading(false);
    return data;
  } catch (error) {
    if (error instanceof CanceledError) {
    } else {
      loading(false);
      console.error(error);
    }
  }
};

const getScores = async (
  location: string,
  mode: string,
  loading: Dispatch<SetStateAction<boolean>>,
  signal: AbortSignal
) => {
  try {
    loading(true);
    const { data } = await http.post('/google/scores/filter', { location, mode }, { signal });
    loading(false);
    return data;
  } catch (error) {
    if (error instanceof CanceledError) {
    } else {
      loading(false);
      console.error(error);
    }
  }
};

const getAllLocations = async (
  offset = 0,
  pageSize = 0,
  loading: Dispatch<SetStateAction<boolean>>,
  signal: AbortSignal,
  isActive?: boolean,
  orderBy?: string[],
  searchTerm?: string,
  filters?: { locationIDs?: string[]; scoreMin?: number; scoreMax?: number }
) => {
  try {
    loading(true);
    const request: any = {
      offset,
      pageSize,
      orderBy,
      isActive,
    };
    if (searchTerm) {
      request.search = searchTerm;
    }
    if (filters?.locationIDs !== undefined) {
      request.locationIDs = filters.locationIDs;
    }
    if (filters?.scoreMin !== undefined) {
      request.scoreMin = filters.scoreMin;
    }
    if (filters?.scoreMax !== undefined) {
      request.scoreMax = filters.scoreMax;
    }
    const { data } = await http.post('/google/locations', request, {
      signal,
    });
    loading(false);
    return data;
  } catch (error) {
    if (error instanceof CanceledError) {
    } else {
      loading(false);
      console.error(error);
    }
  }
};

const toggleLocationActive = async (ID: string, isActive: boolean) => {
  try {
    const { data } = await http.patch('/google/locations/crawler-status', {
      locations: [
        {
          ID,
          isActive,
        },
      ],
    });
    return data;
  } catch (error) {
    console.error(error);
  }
};

const toggleLocationCommenting = async (ID: string, allowReviewResponses: boolean) => {
  try {
    const { data } = await http.patch('/google/locations/allow-response', {
      locations: [
        {
          ID,
          allowReviewResponses,
        },
      ],
    });
    return data;
  } catch (error) {
    console.error(error);
  }
};

const setInterval = async (interval: string) => {
  try {
    const { data } = await http.patch('/google/auth/interval', {
      interval: interval,
    });
    return data;
  } catch (error) {
    console.error(error);
  }
};

const replyToReview = async (
  locationId: string,
  reviewId: string,
  reply: string,
  loading: Dispatch<SetStateAction<boolean>>
) => {
  try {
    loading(true);
    const { data } = await http.put(
      `/google/locations/${locationId}/reviews/${reviewId}`,
      { comment: reply },
      // A dropped connection with no response leaves the shared axios instance waiting forever, which is
      // how the reply spinner used to hang (772363). Scoped to this call: a global default would also cap
      // legitimately slow requests such as POST /google/locations.
      { timeout: 30000 }
    );
    loading(false);
    return data;
  } catch (error) {
    loading(false);
    console.log(error);
  }
};

const updateReviewReply = async (
  locationId: string,
  reviewId: string,
  reply: string,
  loading: Dispatch<SetStateAction<boolean>>
) => {
  try {
    loading(true);
    const { data } = await http.put(`/google/locations/${locationId}/reviews/${reviewId}`, {
      comment: reply,
    });
    loading(false);
    return data;
  } catch (error) {
    loading(false);
    console.log(error);
  }
};

const deleteReviewReply = async (
  postgresLocationId: string,
  reviewId: string,
  loading: Dispatch<SetStateAction<boolean>>
) => {
  try {
    loading(true);
    const { data } = await http.delete(
      `/google/locations/${postgresLocationId}/reviews/${reviewId}`,
      {
        timeout: 30000,
      }
    );
    loading(false);
    return data;
  } catch (error) {
    loading(false);
    console.log(error);
  }
};

const saveCommunityManagementSettings = async (
  locationId: string,
  communityManagement: CommunityManagement,
  loading: Dispatch<SetStateAction<boolean>>
) => {
  try {
    loading(true);
    await http.patch('/google/locations/community-settings', {
      id: locationId,
      communityManagement,
    });
    loading(false);
  } catch (error) {
    loading(false);
    console.error(error);
  }
};

const refreshGmbData = async () => {
  const { data } = await http.post('/google/locations/refresh', {});
  return data;
};

const getRefreshStatus = async () => {
  const { data } = await http.get('/google/locations/refresh/status');
  return data;
};

export type GMBRefreshError = AxiosError<{ data?: { availableAt?: string } }>;

const GMBService = {
  getAllLocations,
  toggleLocationActive,
  toggleLocationCommenting,
  replyToReview,
  deleteReviewReply,
  saveCommunityManagementSettings,
  updateReviewReply,
  getLastScores,
  getScores,
  setInterval,
  refreshGmbData,
  getRefreshStatus,
};

export default GMBService;
